import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TableSortLabel, TextField, Checkbox
} from '@mui/material';

const get = (obj, path) => path.split('.').reduce((o, i) => (o === null || o === undefined ? undefined : o[i]), obj);

const SortableTable = ({
    data,
    columns,
    rowKey = 'id',
    tableProps = {},
    defaultSort = null,
    // Opt-in row selection. When enabled, a leading checkbox column is rendered.
    selectable = false,
    onSelectionChange,   // (selectedRows[]) => void
    onFilteredChange,    // (visibleRows[]) => void  — filtered + sorted rows
    selectionResetToken, // change this value to clear the current selection
}) => {
    const [sortConfig, setSortConfig] = useState(defaultSort);
    const [filters, setFilters] = useState({});
    const [selectedKeys, setSelectedKeys] = useState(() => new Set());

    const filteredData = useMemo(() => {
        return data.filter(row => {
            return Object.entries(filters).every(([key, value]) => {
                if (!value) return true;
                const rowValue = get(row, key);
                return String(rowValue ?? '').toLowerCase().includes(value.toLowerCase());
            });
        });
    }, [data, filters]);

    const sortedData = useMemo(() => {
        let sortableData = [...filteredData];
        if (sortConfig !== null) {
            sortableData.sort((a, b) => {
                const valA = get(a, sortConfig.key);
                const valB = get(b, sortConfig.key);

                // Handle cases where values might be null or undefined
                if (valA == null && valB == null) return 0;
                if (valA == null) return sortConfig.direction === 'ascending' ? -1 : 1;
                if (valB == null) return sortConfig.direction === 'ascending' ? 1 : -1;

                if (valA < valB) {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (valA > valB) {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableData;
    }, [filteredData, sortConfig]);

    // --- Selection / reporting to parent (all opt-in) ---
    const onFilteredChangeRef = useRef(onFilteredChange);
    onFilteredChangeRef.current = onFilteredChange;
    const onSelectionChangeRef = useRef(onSelectionChange);
    onSelectionChangeRef.current = onSelectionChange;

    // Report the visible (filtered + sorted) rows whenever they change.
    useEffect(() => {
        onFilteredChangeRef.current?.(sortedData);
    }, [sortedData]);

    // Selected row objects, derived from keys against the live data.
    const selectedRows = useMemo(
        () => data.filter(row => selectedKeys.has(get(row, rowKey))),
        [data, selectedKeys, rowKey]
    );
    useEffect(() => {
        onSelectionChangeRef.current?.(selectedRows);
    }, [selectedRows]);

    // Allow the parent to clear selection (e.g. after a bulk delete).
    useEffect(() => {
        if (selectionResetToken !== undefined) setSelectedKeys(new Set());
    }, [selectionResetToken]);

    const visibleKeys = useMemo(() => sortedData.map(r => get(r, rowKey)), [sortedData, rowKey]);
    const selectedVisibleCount = visibleKeys.filter(k => selectedKeys.has(k)).length;
    const allVisibleSelected = visibleKeys.length > 0 && selectedVisibleCount === visibleKeys.length;
    const someVisibleSelected = selectedVisibleCount > 0 && !allVisibleSelected;

    const toggleAll = (checked) => {
        setSelectedKeys(prev => {
            const next = new Set(prev);
            visibleKeys.forEach(k => (checked ? next.add(k) : next.delete(k)));
            return next;
        });
    };

    const toggleRow = (key, checked) => {
        setSelectedKeys(prev => {
            const next = new Set(prev);
            if (checked) next.add(key); else next.delete(key);
            return next;
        });
    };

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig && sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value
        }));
    };

    return (
        <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="sortable table" {...tableProps}>
                <TableHead>
                    <TableRow>
                        {selectable && (
                            <TableCell padding="checkbox">
                                <Checkbox
                                    size="small"
                                    checked={allVisibleSelected}
                                    indeterminate={someVisibleSelected}
                                    onChange={(e) => toggleAll(e.target.checked)}
                                    inputProps={{ 'aria-label': 'Select all visible rows' }}
                                />
                            </TableCell>
                        )}
                        {columns.map(col => (
                            <TableCell
                                key={col.key}
                                align={col.align || 'left'}
                                sortDirection={sortConfig?.key === col.key ? (sortConfig.direction === 'ascending' ? 'asc' : 'desc') : false}
                            >
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    {col.sortable !== false ? (
                                        <TableSortLabel
                                            active={sortConfig?.key === col.key}
                                            direction={sortConfig?.key === col.key ? (sortConfig.direction === 'ascending' ? 'asc' : 'desc') : 'asc'}
                                            onClick={() => requestSort(col.key)}
                                        >
                                            {col.header}
                                        </TableSortLabel>
                                    ) : (
                                        <span>{col.header}</span>
                                    )}
                                    {col.filterable !== false && (
                                        <TextField
                                            size="small"
                                            variant="standard"
                                            placeholder="Filter..."
                                            value={filters[col.key] || ''}
                                            onChange={(e) => handleFilterChange(col.key, e.target.value)}
                                            onClick={(e) => e.stopPropagation()} // Prevent sort trigger
                                            sx={{ mt: 1 }}
                                        />
                                    )}
                                </div>
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {sortedData.map((row) => {
                        const key = get(row, rowKey);
                        return (
                        <TableRow key={key} hover selected={selectable && selectedKeys.has(key)}>
                            {selectable && (
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        size="small"
                                        checked={selectedKeys.has(key)}
                                        onChange={(e) => toggleRow(key, e.target.checked)}
                                        inputProps={{ 'aria-label': 'Select row' }}
                                    />
                                </TableCell>
                            )}
                            {columns.map(col => (
                                <TableCell key={col.key} align={col.align || 'left'}>
                                    {col.render ? col.render(row) : get(row, col.key)}
                                </TableCell>
                            ))}
                        </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default SortableTable;
